import { useEffect, useRef, useState } from "react";
import { callIsg } from "./data";

// Preferences never grant access; the server binds each preference to auth.uid.
export function useFacilityPreference(user, name, fallback) {
  const owner = user?.uid || user?.email || "";
  const key = owner ? `isg:facilities:${owner}:${name}` : "";
  const valid = value => typeof fallback === "string" ? typeof value === "string" : Array.isArray(value) && value.every(id => typeof id === "string");
  const read = () => {
    try {
      const raw = localStorage.getItem(key) ?? (user?.email ? localStorage.getItem(`isg:facilities:${user.email}:${name}`) : null);
      const value = JSON.parse(raw);
      return valid(value) ? value : fallback;
    } catch { return fallback; }
  };
  const [state, setState] = useState(() => ({ key, value: read(), error: "" }));
  const currentKey = useRef(key); currentKey.current = key;
  const changes = useRef(0);
  const queue = useRef(Promise.resolve());
  const cache = value => { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* Cloud storage remains available. */ } };
  useEffect(() => {
    if (!owner) return undefined;
    let cancelled = false;
    const version = changes.current;
    const local = read();
    setState({ key, value: local, error: "" });
    const load = async () => {
      try {
        const result = await callIsg("isgGetFacilityPreference", { name, ownerUid: user?.uid });
        if (cancelled || version !== changes.current) return;
        if (result?.found && valid(result.value)) {
          cache(result.value); setState({ key, value: result.value, error: "" });
        } else if (valid(local)) {
          queue.current = queue.current.catch(() => {}).then(() => currentKey.current === key && version === changes.current ? callIsg("isgSaveFacilityPreference", { name, value: local, ownerUid: user?.uid }) : undefined);
          await queue.current;
        }
      } catch {
        if (!cancelled) setState(previous => ({ ...previous, error: "Tesis se\u00e7imi hesaptan al\u0131namad\u0131; bu taray\u0131c\u0131daki son se\u00e7im kullan\u0131l\u0131yor." }));
      }
    };
    load();
    return () => { cancelled = true; };
    // Each load belongs to one account/field; later local changes win over it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const update = value => {
    changes.current += 1;
    cache(value); setState({ key, value, error: "" });
    if (!owner) return;
    queue.current = queue.current.catch(() => {}).then(() => currentKey.current === key ? callIsg("isgSaveFacilityPreference", { name, value, ownerUid: user?.uid }) : undefined).then(() => {
      if (currentKey.current === key) setState(previous => ({ ...previous, error: "" }));
    }).catch(() => {
      if (currentKey.current === key) setState(previous => ({ ...previous, error: "Tesis se\u00e7imi bu taray\u0131c\u0131da sakland\u0131; hesaba kaydedilemedi. Ba\u011flant\u0131y\u0131 ve sunucu yay\u0131n\u0131n\u0131 kontrol edin." }));
    });
  };
  return [state.key === key ? state.value : read(), update, state.key === key ? state.error : ""];
}
